import { useState, useEffect, useCallback } from "react";
import {
  GEOFENCE_STATUS,
  getCurrentGPSLocation,
  evaluateLocationStatus,
  fetchSiteGeoFence,
  validateSiteLocationWithBackend,
} from "../services/geofenceService";

export function useGeoFence(siteId, options = {}) {
  const { autoFetch = true, autoCapture = false } = options;

  const [siteConfig, setSiteConfig] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [distance, setDistance] = useState(null);
  const [status, setStatus] = useState(GEOFENCE_STATUS.IDLE);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState(null);

  // Load site configuration
  const loadSiteConfig = useCallback(async () => {
    if (!siteId) {
      setSiteConfig(null);
      setStatus(GEOFENCE_STATUS.IDLE);
      return null;
    }

    setIsLoading(true);
    setError(null);
    try {
      const config = await fetchSiteGeoFence(siteId);
      setSiteConfig(config);

      if (!config.is_geofence_configured) {
        setStatus(GEOFENCE_STATUS.SITE_GEOFENCE_NOT_CONFIGURED);
        setMessage(
          `Site location has not been configured for ${config.name || "this site"}. Please contact an administrator.`
        );
      } else if (config.geofence_enabled === false) {
        setStatus(GEOFENCE_STATUS.SITE_GEOFENCE_DISABLED);
        setMessage(`Site geo-fencing is disabled for ${config.name || "this site"}.`);
      }

      return config;
    } catch (err) {
      setError(err);
      setStatus(GEOFENCE_STATUS.SITE_GEOFENCE_NOT_CONFIGURED);
      setMessage("Failed to load site geofence configuration.");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  // Request browser location and evaluate
  const captureCurrentLocation = useCallback(async () => {
    setIsCapturing(true);
    setError(null);

    // Determine active site config FIRST before requesting GPS
    let activeConfig = siteConfig;
    if (!activeConfig && siteId) {
      activeConfig = await loadSiteConfig();
    }

    if (!activeConfig || !activeConfig.is_geofence_configured || activeConfig.geofence_enabled === false) {
      setIsCapturing(false);
      const isDis = activeConfig?.geofence_enabled === false;
      const targetStatus = isDis ? GEOFENCE_STATUS.SITE_GEOFENCE_DISABLED : GEOFENCE_STATUS.SITE_GEOFENCE_NOT_CONFIGURED;
      const targetMsg = isDis
        ? `Geo-fencing is disabled for ${activeConfig?.name || "this site"}.`
        : `Site location has not been configured for ${activeConfig?.name || "this site"}.`;
      setStatus(targetStatus);
      setMessage(targetMsg);
      return { coords: null, allowed: true, isBypassed: true };
    }

    setStatus(GEOFENCE_STATUS.LOADING);
    setMessage("Acquiring high-accuracy GPS coordinates...");

    try {
      const coords = await getCurrentGPSLocation();
      setUserLocation(coords);
      setAccuracy(coords.accuracy);

      // 1. Client-side evaluation for instant visual feedback
      const localEval = evaluateLocationStatus({
        userLat: coords.latitude,
        userLon: coords.longitude,
        accuracy: coords.accuracy,
        siteConfig: activeConfig,
      });

      setDistance(localEval.distance);
      setStatus(localEval.status);
      setMessage(localEval.message);

      // 2. Server-side validation
      try {
        const backendResult = await validateSiteLocationWithBackend(siteId, {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
        });

        setStatus(backendResult.status);
        setMessage(backendResult.message);
        if (backendResult.distance !== null && backendResult.distance !== undefined) {
          setDistance(backendResult.distance);
        }

        return {
          coords,
          allowed: backendResult.allowed,
          status: backendResult.status,
          distance: backendResult.distance,
          accuracy: coords.accuracy,
          siteId,
        };
      } catch (backendErr) {
        // Fallback to local evaluation if network drops
        return {
          coords,
          allowed: localEval.allowed,
          status: localEval.status,
          distance: localEval.distance,
          accuracy: coords.accuracy,
          siteId,
        };
      }
    } catch (geoErr) {
      setError(geoErr);
      setStatus(geoErr.status || GEOFENCE_STATUS.LOCATION_UNAVAILABLE);
      setMessage(geoErr.userMessage || geoErr.message || "Failed to acquire location.");
      return { coords: null, allowed: false, error: geoErr };
    } finally {
      setIsCapturing(false);
    }
  }, [siteId, siteConfig, loadSiteConfig]);

  useEffect(() => {
    if (autoFetch && siteId) {
      loadSiteConfig();
    }
  }, [autoFetch, siteId, loadSiteConfig]);

  useEffect(() => {
    if (autoCapture && siteId && siteConfig?.is_geofence_configured) {
      captureCurrentLocation();
    }
  }, [autoCapture, siteId, siteConfig?.is_geofence_configured, captureCurrentLocation]);

  return {
    siteConfig,
    userLocation,
    accuracy,
    distance,
    status,
    message,
    isLoading,
    isCapturing,
    error,
    captureCurrentLocation,
    refreshSiteConfig: loadSiteConfig,
    isValidated: status === GEOFENCE_STATUS.WITHIN_SITE || status === GEOFENCE_STATUS.SITE_GEOFENCE_DISABLED,
  };
}
